import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useTranslation } from "react-i18next";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { createTeamDm, sendTeamMessage } from "../../api/client";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { usePortalTeamMessages } from "../../hooks/usePortalTeamMessages";
import { usePortalTeamActiveSpace } from "../../hooks/usePortalTeamActiveSpace";
import { usePortalTeamDirectory } from "../../hooks/usePortalTeamDirectory";
import { usePortalTeamSocketWorkspace } from "../../hooks/usePortalTeamSocketWorkspace";
import { useRealtimeTeamMessages } from "../../hooks/useRealtimeTeamMessages";
import TeamMessagePane from "../Team/panes/TeamMessagePane";
import PortalTeamSidebar, { flatConversationsFromDirectory } from "./PortalTeamSidebar";
import {
  clearPortalConversationUnread,
  patchPortalConversation,
} from "./portalTeamDirectoryState";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";
import { getTeamPaneContainerStyle, TEAM_MOBILE_MEDIA_QUERY } from "../Team/utils/teamUiUtils";
import { useNotificationsOptional } from "../../context/NotificationContext";

const PORTAL_MESSAGE_POLL_MS = 25_000;

const PortalTeamPage = ({ mode = "platform" }) => {
  const { t } = useTranslation("team");
  const { t: tApp } = useTranslation("app");
  const theme = useTheme();
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const [searchParams, setSearchParams] = useSearchParams();

  const [openingUserKey, setOpeningUserKey] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [, setI18nReady] = useState(0);

  const activeWorkspace = searchParams.get("ws") || "";
  const activeSpaceId = searchParams.get("space") ? Number(searchParams.get("space")) : null;

  const notificationsCtx = useNotificationsOptional();
  const outletCtx = useOutletContext();
  const {
    directory,
    isInitialLoading,
    patchDirectory,
    refreshDirectory,
    refreshWorkspace,
    loadWorkspace,
    scheduleDirectorySync,
    isWorkspaceLoading,
    isWorkspaceLoaded,
  } = usePortalTeamDirectory(mode);

  usePortalTeamSocketWorkspace(activeWorkspace);

  useEffect(() => {
    if (activeWorkspace) void loadWorkspace(activeWorkspace);
  }, [activeWorkspace, loadWorkspace]);

  const workspaceLabelBySlug = useMemo(() => {
    const map = new Map();
    for (const ws of directory) {
      if (ws?.workspaceSlug) map.set(ws.workspaceSlug, ws.workspaceLabel || ws.workspaceSlug);
    }
    return map;
  }, [directory]);

  const inbox = useMemo(() => flatConversationsFromDirectory(directory), [directory]);

  const spaceToWorkspace = useMemo(() => {
    const map = new Map();
    for (const c of inbox) {
      map.set(Number(c.id), c.workspaceSlug);
    }
    return map;
  }, [inbox]);

  const {
    space: activeSpace,
    members,
    tenantUserId,
    loading: activeSpaceLoading,
  } = usePortalTeamActiveSpace(activeSpaceId, activeWorkspace);

  const {
    messages,
    loadMessages,
    loadMore,
    syncLatest,
    syncRecentMessages,
    sendMessage,
    isSending,
    isLoading: messagesLoading,
    hasMore,
    markRead,
    reset,
  } = usePortalTeamMessages(activeSpaceId, activeWorkspace);

  const paneSpace = useMemo(() => {
    if (!activeSpace || !activeWorkspace) return null;
    return {
      ...activeSpace,
      workspaceSlug: activeWorkspace,
      workspaceLabel: workspaceLabelBySlug.get(activeWorkspace) || activeWorkspace,
    };
  }, [activeSpace, activeWorkspace, workspaceLabelBySlug]);

  const sameWorkspaceSpaces = useMemo(
    () => inbox.filter((s) => s.workspaceSlug === activeWorkspace),
    [inbox, activeWorkspace]
  );

  const containerStyle = useMemo(
    () => getTeamPaneContainerStyle(theme, isMobile),
    [theme, isMobile]
  );

  const refreshDirectoryRef = useRef(refreshDirectory);
  refreshDirectoryRef.current = refreshDirectory;

  useEffect(() => {
    void ensureNamespaceLoaded("team").then(() => setI18nReady((n) => n + 1));
  }, []);

  const setTeamPageFocus = notificationsCtx?.setTeamPageFocus;

  useEffect(() => {
    setTeamPageFocus?.(activeSpaceId ?? null);
    return () => setTeamPageFocus?.(null);
  }, [setTeamPageFocus, activeSpaceId]);

  useEffect(() => {
    const registerRefresh = outletCtx?.registerRefresh;
    const stopRefreshing = outletCtx?.stopRefreshing;
    if (!registerRefresh) return undefined;
    registerRefresh(() => {
      refreshDirectoryRef.current().finally(() => stopRefreshing?.());
    });
    return () => registerRefresh(() => stopRefreshing?.());
  }, [outletCtx?.registerRefresh, outletCtx?.stopRefreshing]);

  useEffect(() => {
    reset();
    if (!activeSpaceId || !activeWorkspace) return;
    loadMessages().catch((err) => {
      setErrorMsg(getApiErrorMessage(err, "Não foi possível carregar as mensagens."));
    });
    markRead()
      .catch(() => {})
      .finally(() => {
        patchDirectory((prev) =>
          clearPortalConversationUnread(prev, activeWorkspace, activeSpaceId)
        );
      });
  }, [activeSpaceId, activeWorkspace, reset, loadMessages, markRead, patchDirectory]);

  const handleDirectorySync = useCallback(
    (spaceId) => {
      const sid = spaceId != null ? Number(spaceId) : null;
      const ws =
        sid && Number.isFinite(sid) && sid > 0
          ? spaceToWorkspace.get(sid) || activeWorkspace
          : activeWorkspace;
      scheduleDirectorySync(ws || undefined);
    },
    [scheduleDirectorySync, spaceToWorkspace, activeWorkspace]
  );

  useRealtimeTeamMessages({
    selectedSpaceId: activeSpaceId,
    syncLatestMessages: syncLatest,
    refetchSpaces: handleDirectorySync,
    markRead,
  });

  useEffect(() => {
    if (!activeSpaceId || !activeWorkspace) return undefined;
    const timer = window.setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      syncLatest().catch(() => {});
    }, PORTAL_MESSAGE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [activeSpaceId, activeWorkspace, syncLatest]);

  const selectConversation = useCallback(
    (workspaceSlug, spaceId) => {
      const next = new URLSearchParams(searchParams);
      next.set("ws", workspaceSlug);
      next.set("space", String(spaceId));
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const handleNavigateToQuotedSpace = useCallback(
    (targetSpaceId) => {
      const sid = Number(targetSpaceId);
      if (!Number.isFinite(sid) || sid <= 0) return;
      const ws = spaceToWorkspace.get(sid) || activeWorkspace;
      if (!ws) return;
      selectConversation(ws, sid);
    },
    [spaceToWorkspace, activeWorkspace, selectConversation]
  );

  const handleOpenUser = useCallback(
    async (ws, user, existingDm) => {
      if (!ws?.workspaceSlug || !user?.userId) return;
      const userKey = `${ws.workspaceSlug}:${user.userId}`;
      if (existingDm?.id) {
        selectConversation(ws.workspaceSlug, existingDm.id);
        return;
      }
      setOpeningUserKey(userKey);
      try {
        const res = await createTeamDm({ userId: user.userId }, ws.workspaceSlug);
        const spaceId = res?.id || res?.data?.id;
        if (!spaceId) throw new Error("DM não criada");
        await refreshWorkspace(ws.workspaceSlug);
        selectConversation(ws.workspaceSlug, spaceId);
      } catch (err) {
        setErrorMsg(getApiErrorMessage(err, t("dmOpenError")));
      } finally {
        setOpeningUserKey(null);
      }
    },
    [refreshWorkspace, selectConversation, t]
  );

  const handleSend = useCallback(
    async (payload) => {
      await sendMessage(payload);
      await syncLatest();
      const preview = String(payload?.content || payload?.text || "").trim();
      const now = new Date().toISOString();
      patchDirectory((prev) =>
        patchPortalConversation(prev, activeWorkspace, activeSpaceId, {
          lastMessagePreview: preview || undefined,
          lastMessageAt: now,
          unreadCount: 0,
          hasUnread: false,
        })
      );
    },
    [sendMessage, syncLatest, patchDirectory, activeWorkspace, activeSpaceId]
  );

  const handleForwardMessage = useCallback(
    async (payload) => {
      await sendTeamMessage(payload, activeWorkspace);
      if (Number(payload?.spaceId) === Number(activeSpaceId)) {
        await syncLatest();
      }
      const targetWs = activeWorkspace;
      const targetSpace = Number(payload?.spaceId);
      if (targetWs && targetSpace > 0) {
        scheduleDirectorySync(targetWs);
      }
    },
    [activeWorkspace, activeSpaceId, syncLatest, scheduleDirectorySync]
  );

  const showList = !isMobile || !activeSpaceId;
  const showThread = !isMobile || Boolean(activeSpaceId);
  const isSinglePaneThread = showThread && !showList;
  const threadReady = Boolean(activeSpaceId && activeWorkspace && paneSpace);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        width: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        alignSelf: "stretch",
      }}
    >
      {showList ? (
        <PortalTeamSidebar
          containerStyle={containerStyle}
          isMobile={isMobile}
          mode={mode}
          directory={directory}
          loading={isInitialLoading}
          activeWorkspace={activeWorkspace}
          activeSpaceId={activeSpaceId}
          onSelectConversation={selectConversation}
          onOpenUser={handleOpenUser}
          openingUserKey={openingUserKey}
          onLoadWorkspace={loadWorkspace}
          isWorkspaceLoading={isWorkspaceLoading}
          isWorkspaceLoaded={isWorkspaceLoaded}
        />
      ) : null}

      {showThread ? (
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {threadReady ? (
            <TeamMessagePane
              containerStyle={containerStyle}
              isMobile={isMobile}
              space={paneSpace}
              members={members}
              spaces={sameWorkspaceSpaces}
              messages={messages}
              currentUserId={tenantUserId}
              teamCompany={activeWorkspace}
              portalTeamMode
              onSend={handleSend}
              onForwardMessage={handleForwardMessage}
              onSyncRecentMessages={syncRecentMessages}
              isSending={isSending}
              isLoading={messagesLoading || activeSpaceLoading}
              hasMore={hasMore}
              onLoadMore={loadMore}
              onNavigateToQuotedSpace={handleNavigateToQuotedSpace}
              onBack={
                isSinglePaneThread
                  ? () => {
                      const next = new URLSearchParams(searchParams);
                      next.delete("space");
                      setSearchParams(next);
                    }
                  : undefined
              }
            />
          ) : activeSpaceId && activeWorkspace && activeSpaceLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...containerStyle,
                bgcolor: "background.paper",
                color: "text.primary",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                textAlign: "center",
                bgcolor: "background.paper",
                color: "text.primary",
                ...containerStyle,
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    mb: 2,
                    mx: "auto",
                  }}
                >
                  <ForumOutlinedIcon sx={{ fontSize: 36, color: "primary.main" }} />
                </Box>
                <Typography variant="h6" color="text.primary" fontWeight={600} gutterBottom>
                  {t("noSpaceSelectedTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                  {tApp("platformPage.portalTeamEmpty", {
                    defaultValue:
                      "Busque um usuário acima ou escolha uma conversa. As mensagens aparecem na Equipe do workspace dele.",
                  })}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      ) : null}

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PortalTeamPage;
